import HttpService from './http-service';
import EdudoResponse from '../models/edudo-response';

export default class FileStorageService {
  private schoolUuid: string;

  constructor(schoolUuid: string) {
    this.schoolUuid = schoolUuid;
  }

  private basePath(): string {
    return `/v1/schools/${this.schoolUuid}/files`;
  }

  async requestUploadTicket(body: {
    fileName: string;
    contentType: string;
    size: number;
    studentUuid?: string;
  }): Promise<FileUploadTicket> {
    const response = await HttpService.postJson<EdudoResponse<FileUploadTicket>>(
      this.basePath(),
      body
    );
    return response.data;
  }

  // Upload direto para o S3. Não passa pelo HttpService: o S3 não recebe o
  // header Authorization e não devolve o envelope da API do Edudo. Os campos
  // assinados vão primeiro e o arquivo (campo "file") por último — ordem exigida
  // pelo S3.
  async uploadToS3(ticket: FileUploadTicket, file: File): Promise<void> {
    const formData = new FormData();
    Object.entries(ticket.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    const response = await fetch(ticket.uploadUrl, {
      method: 'POST',
      mode: 'cors',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Falha no upload do arquivo (${response.status}).`);
    }
  }

  // O atestado fica vinculado ao aluno no upload: a justificativa recusa um
  // arquivo que não seja do aluno ausente.
  async uploadSickNote(file: File, studentUuid: string): Promise<string> {
    return this.uploadFile(file, studentUuid);
  }

  // Fluxo genérico de upload (ticket + upload para o S3), usado por qualquer
  // feature que precise anexar um arquivo (ex: imagem/anexos de comunicados).
  async uploadFile(file: File, studentUuid?: string): Promise<string> {
    const ticket = await this.requestUploadTicket({
      fileName: file.name,
      contentType: file.type,
      size: file.size,
      ...(studentUuid && { studentUuid }),
    });

    await this.uploadToS3(ticket, file);

    return ticket.fileId;
  }

  // Upload de vários arquivos com sucesso parcial: um arquivo que falha não
  // derruba os outros. Usado pela galeria, que envia lotes de imagens de uma vez
  // e precisa reportar quais falharam sem perder as que subiram.
  async uploadFilesWithPartialSuccess(
    files: FileList | File[],
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<GalleryFileUploadResult> {
    const filesArray = Array.from(files as ArrayLike<File>);
    const concurrencyLimit = 3;
    const successful: GalleryFileDto[] = [];
    const failed: { file: File; error: any }[] = [];
    let uploadedCount = 0;

    const uploadSingleFile = async (file: File): Promise<GalleryFileDto | null> => {
      try {
        const fileId = await this.uploadFile(file);

        // A API só devolve a url assinada depois que o S3 confirma o upload, então
        // até a próxima leitura da galeria usamos um preview local para as
        // miniaturas do formulário não ficarem quebradas.
        return { fileId, name: file.name, url: URL.createObjectURL(file) };
      } catch (error) {
        failed.push({ file, error });

        return null;
      } finally {
        uploadedCount++;
        if (onProgress) onProgress(uploadedCount, filesArray.length);
      }
    };

    for (let i = 0; i < filesArray.length; i += concurrencyLimit) {
      const batch = filesArray.slice(i, i + concurrencyLimit);
      const results = await Promise.all(batch.map(uploadSingleFile));

      results.forEach((result) => {
        if (result) successful.push(result);
      });
    }

    return { successful, failed };
  }

  async getFile(fileId: string): Promise<FileDownload> {
    const response = await HttpService.getJson<EdudoResponse<FileDownload>>(
      `${this.basePath()}/${fileId}`
    );
    return response.data;
  }
}
